import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  Search,
  MapPin,
  Phone,
  Globe,
  RefreshCw,
  ChevronRight,
  Store,
  GraduationCap,
  Utensils,
  Heart,
  Briefcase
} from 'lucide-react';

const categories = [
  { value: 'all', label: 'All', icon: Building2 },
  { value: 'restaurant', label: 'Restaurants', icon: Utensils },
  { value: 'retail', label: 'Retail', icon: Store },
  { value: 'service', label: 'Services', icon: Briefcase },
  { value: 'school', label: 'Schools', icon: GraduationCap },
  { value: 'healthcare', label: 'Healthcare', icon: Heart }
];

const BusinessDirectoryPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const params = category !== 'all' ? { category } : {};
        const response = await axios.get(`${API}/businesses`, { params });
        setBusinesses(response.data);
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, [category]);

  const filteredBusinesses = businesses.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Building2 className="w-10 h-10" />
            Business Directory
          </h1>
          <p className="mt-2 text-white/80 max-w-2xl">
            Discover and support local businesses and schools in the Killarney community.
          </p>
          
          {/* Search */}
          <div className="mt-6 max-w-lg">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search businesses..."
                className="pl-12 py-6 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white focus:text-slate-900"
                data-testid="business-search"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categories */}
        <Tabs value={category} onValueChange={setCategory} className="space-y-6">
          <TabsList className="bg-white border flex-wrap h-auto gap-1 p-1">
            {categories.map((cat) => (
              <TabsTrigger key={cat.value} value={cat.value} className="flex items-center gap-2" data-testid={`category-${cat.value}`}>
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={category}>
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredBusinesses.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No businesses found</p>
                  <p className="text-sm text-slate-400 mt-2">
                    {search ? 'Try a different search term' : 'Check back soon for new listings!'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBusinesses.map((business) => (
                  <Link key={business.id} to={`/directory/${business.id}`} data-testid={`business-card-${business.id}`}>
                    <Card className="bg-white h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-0">
                        {business.cover_url ? (
                          <img src={business.cover_url} alt={business.name} className="w-full h-40 object-cover" />
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            {business.logo_url ? (
                              <img src={business.logo_url} alt={business.name} className="w-20 h-20 object-contain" />
                            ) : (
                              <Building2 className="w-12 h-12 text-slate-300" />
                            )}
                          </div>
                        )}
                        <div className="p-4">
                          <Badge variant="outline" className="mb-2 capitalize">{business.category}</Badge>
                          <h3 className="font-heading font-bold text-lg text-slate-900">{business.name}</h3>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{business.description}</p>
                          
                          <div className="mt-4 space-y-1 text-sm text-slate-500">
                            <p className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="truncate">{business.address}</span>
                            </p>
                            {business.phone && (
                              <p className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                {business.phone}
                              </p>
                            )}
                          </div>
                          
                          <div className="mt-4 flex items-center text-emerald-600 text-sm font-medium">
                            View Profile <ChevronRight className="w-4 h-4 ml-1" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Business CTA */}
        <Card className="mt-12 bg-emerald-50 border-emerald-200">
          <CardContent className="p-8 text-center">
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
              Own a Business in Killarney?
            </h3>
            <p className="text-slate-600 mb-4">
              Add your business to our directory and connect with the community!
            </p>
            <Link to="/my-business">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Building2 className="w-4 h-4 mr-2" />
                List Your Business
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessDirectoryPage;
